import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { mockQuotationComparisonService } from '@/services/mock/MockQuotationComparisonService';
import { QuotationFilterState, QuotationMetricsSummary, QuotationRecord } from '@/types/domain/quotation-comparison';
import { QuotationKpiCards } from '@/components/quotations/QuotationKpiCards';
import { DataTable, ColumnDef } from '@/components/data-display/DataTable';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { formatInr, formatDate } from '@/lib/utils';
import {
  Layers,
  RefreshCw,
  Search,
  Sparkles,
  Trophy,
  Eye,
  Pencil,
  Trash2,
  AlertTriangle,
  Loader2,
  X,
} from 'lucide-react';
import { ROUTES } from '@/constants/routes';
import { SlideOverDrawer } from '@/components/ui/SlideOverDrawer';
import { QuotationComparisonPage } from './QuotationComparisonPage';

export const QuotationsListPage: React.FC = () => {
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState<QuotationMetricsSummary | null>(null);
  const [records, setRecords] = useState<QuotationRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Deletion Modal State
  const [quoteToDelete, setQuoteToDelete] = useState<QuotationRecord | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedRfqForDrawer, setSelectedRfqForDrawer] = useState<string | null>(null);

  const [filters, setFilters] = useState<QuotationFilterState>({
    search: '',
    rfqId: 'ALL',
    status: 'ALL',
    page: 1,
    pageSize: 25,
  });

  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      const [m, r] = await Promise.all([
        mockQuotationComparisonService.getQuotationMetrics(),
        mockQuotationComparisonService.getQuotations(filters),
      ]);
      setMetrics(m);
      setRecords(r.data);
    } finally {
      setIsLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (!quoteToDelete) return;
    const orig = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = orig;
    };
  }, [quoteToDelete]);

  const handleConfirmDelete = async () => {
    if (!quoteToDelete) return;
    try {
      setIsDeleting(true);
      await mockQuotationComparisonService.deleteQuotation(quoteToDelete.id);
      setQuoteToDelete(null);
      await loadData();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to withdraw quotation.');
    } finally {
      setIsDeleting(false);
    }
  };

  const columns: ColumnDef<QuotationRecord>[] = [
    {
      header: 'Quotation #',
      accessorKey: 'quotationNumber',
      sortable: true,
      cell: (row) => (
        <div className="space-y-0.5 font-poppins">
          <span
            onClick={() => navigate(ROUTES.QUOTATIONS.COMPARISON(row.rfqId))}
            className="font-poppins font-bold text-primary hover:underline cursor-pointer whitespace-nowrap text-sm block"
          >
            {row.quotationNumber}
          </span>
          <span className="text-xs text-foreground-muted font-mono block whitespace-nowrap">
            RFQ: {row.rfqNumber}
          </span>
        </div>
      ),
    },
    {
      header: 'Supplier & Legal Entity',
      accessorKey: 'vendorName',
      sortable: true,
      cell: (row) => (
        <div className="flex flex-col min-w-0 max-w-[200px] xl:max-w-[240px] font-poppins">
          <span className="font-bold text-foreground text-sm truncate block" title={row.vendorName}>
            {row.vendorName}
          </span>
          <span className="text-xs text-foreground-muted font-mono truncate block">
            Vendor Code: {row.vendorCode}
          </span>
        </div>
      ),
    },
    {
      header: 'Evaluated TCO',
      accessorKey: 'evaluatedCost',
      sortable: true,
      cell: (row) => (
        <div className="font-poppins">
          <span className="font-bold text-foreground font-mono whitespace-nowrap text-sm block">
            {formatInr(row.evaluatedCost)}
          </span>
          <span className="text-[11px] text-foreground-muted block">
            Base: {formatInr(row.subtotalAmount)}
          </span>
        </div>
      ),
    },
    {
      header: 'Score & Rank',
      cell: (row) => (
        <div className="font-poppins space-y-0.5 whitespace-nowrap">
          <div className="flex items-center gap-1 text-primary font-bold text-xs">
            <Sparkles className="h-3.5 w-3.5" />
            <span>{row.overallScore}/100</span>
          </div>
          {row.rank === 1 ? (
            <span className="inline-flex items-center gap-1 text-[10px] font-extrabold text-amber-600 bg-amber-500/10 border border-amber-500/20 px-2 py-0.5 rounded-full">
              <Trophy className="h-3 w-3 fill-current" /> L1 Lowest Bid
            </span>
          ) : (
            <span className="text-[11px] text-foreground-muted font-medium">Rank #{row.rank}</span>
          )}
        </div>
      ),
    },
    {
      header: 'Submitted Date',
      cell: (row) => (
        <span className="text-xs text-foreground-secondary font-mono font-medium whitespace-nowrap font-poppins">
          {formatDate(row.submittedAt)}
        </span>
      ),
    },
    {
      header: 'Status',
      accessorKey: 'status',
      cell: (row) => (
        <div className="flex items-center font-poppins">
          {row.isSelected ? (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border shadow-2xs font-poppins bg-emerald-500/10 text-emerald-600 border-emerald-500/20 whitespace-nowrap">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 shrink-0" />
              Awarded (L1)
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border shadow-2xs font-poppins bg-primary/10 text-primary border-primary/20 whitespace-nowrap">
              <span className="h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
              Submitted
            </span>
          )}
        </div>
      ),
    },
    {
      header: 'Actions',
      cell: (row) => (
        <div
          className="flex items-center gap-1.5 justify-start shrink-0 whitespace-nowrap font-poppins min-w-[110px]"
          onClick={(e) => e.stopPropagation()}
        >
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSelectedRfqForDrawer(row.rfqId)}
            title="Compare in Quotation Matrix (Slide-over)"
            className="h-8 w-8 p-0 rounded-lg hover:bg-primary/15 hover:text-primary active:scale-90 transition-all duration-200 cursor-pointer text-foreground-secondary shrink-0 inline-flex items-center justify-center group shadow-2xs hover:shadow-xs"
          >
            <Eye className="h-4 w-4 transition-transform duration-200 group-hover:scale-115" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(ROUTES.QUOTATIONS.COMPARISON(row.rfqId))}
            title="Evaluate Terms"
            className="h-8 w-8 p-0 rounded-lg hover:bg-primary/10 cursor-pointer text-primary hover:text-primary-dark shrink-0 inline-flex items-center justify-center"
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setQuoteToDelete(row)}
            title="Withdraw Quotation"
            className="h-8 w-8 p-0 rounded-lg text-rose-500 hover:text-rose-600 hover:bg-rose-500/15 cursor-pointer shrink-0 inline-flex items-center justify-center"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6 pb-12 font-poppins">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-foreground flex items-center gap-2.5 font-poppins">
            <Sparkles className="h-7 w-7 text-primary" /> Quotation Matrix & Strategic Sourcing Evaluation
          </h1>
          <p className="text-xs text-foreground-secondary font-medium mt-1 font-poppins">
            Multi-vendor commercial & technical quotation comparison matrix, AI decision scoring, and automated purchase order release.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={loadData}
            className="rounded-xl font-bold font-poppins cursor-pointer"
          >
            <RefreshCw className="h-4 w-4 mr-1.5" /> Refresh
          </Button>

          <Button
            onClick={() => navigate(ROUTES.RFQ.LIST)}
            className="rounded-xl font-bold bg-primary hover:bg-primary/90 text-primary-foreground font-poppins cursor-pointer shadow-xs"
          >
            <Layers className="h-4 w-4 mr-1.5" /> RFQ Sourcing Center
          </Button>
        </div>
      </div>

      {/* KPI Cards Strip */}
      {metrics && <QuotationKpiCards metrics={metrics} />}

      {/* Main Ledger Card with Squeezed Status Tabs & Search */}
      <Card className="border-border rounded-2xl shadow-xs overflow-hidden font-poppins">
        <CardHeader className="p-4 border-b border-border/60 bg-muted/20">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
            {/* Squeezed Status Tabs */}
            <div className="flex flex-wrap items-center gap-1.5">
              {[
                { key: 'ALL', label: 'All Quotations' },
                { key: 'SELECTED', label: 'Awarded (L1)' },
                { key: 'SUBMITTED', label: 'Under Review' },
              ].map((tab) => {
                const isActive = filters.status === tab.key;
                return (
                  <button
                    key={tab.key}
                    onClick={() => setFilters((prev) => ({ ...prev, status: tab.key }))}
                    className={`px-3.5 py-1.5 rounded-xl text-xs font-semibold transition-all cursor-pointer font-poppins ${
                      isActive
                        ? 'bg-primary text-primary-foreground font-bold shadow-xs'
                        : 'bg-card border border-border text-foreground-secondary hover:bg-muted hover:text-foreground'
                    }`}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>

            {/* Search Input */}
            <div className="relative min-w-[240px]">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground-muted" />
              <input
                type="text"
                value={filters.search}
                onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
                placeholder="Search Quote #, RFQ #, Vendor..."
                className="w-full h-9 pl-9 pr-3 text-xs font-medium rounded-xl border border-input bg-background text-foreground placeholder:text-foreground-muted focus:outline-none focus:ring-1 focus:ring-primary font-poppins"
              />
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-4 overflow-x-auto">
          <DataTable
            data={records}
            columns={columns}
            isLoading={isLoading}
            pageSize={25}
            totalCount={records.length}
            searchPlaceholder="Search quotation bids..."
            fixedHeight={true}
            containerHeightClass="max-h-[580px] overflow-y-auto overflow-x-auto custom-scrollbar"
            emptyTitle="No Quotations Found"
            emptyDescription="There are no supplier quotations matching your current search criteria."
            onRowClick={(row) => navigate(ROUTES.QUOTATIONS.COMPARISON(row.rfqId))}
          />
        </CardContent>
      </Card>

      {/* Delete Confirmation Modal (Mounted via createPortal to document.body) */}
      {quoteToDelete &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/75 backdrop-blur-sm p-4 animate-in fade-in"
            onClick={() => !isDeleting && setQuoteToDelete(null)}
          >
            <div
              className="w-full max-w-md rounded-2xl border border-border bg-card p-6 shadow-2xl space-y-4 font-poppins animate-in zoom-in-95 my-auto text-foreground"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between border-b border-border/60 pb-3">
                <div className="flex items-center gap-2 text-rose-600">
                  <AlertTriangle className="h-5 w-5" />
                  <h3 className="text-base font-bold text-foreground font-poppins">
                    Withdraw Supplier Quotation?
                  </h3>
                </div>
                <button
                  onClick={() => !isDeleting && setQuoteToDelete(null)}
                  className="rounded-lg p-1 hover:bg-muted text-foreground-muted cursor-pointer"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {/* Quote Details Box */}
              <div className="rounded-xl bg-muted/40 border border-border p-3.5 space-y-2 text-xs">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-foreground-muted uppercase tracking-wider text-[10px]">
                    Quotation #
                  </span>
                  <span className="font-mono font-bold text-primary">
                    {quoteToDelete.quotationNumber}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-bold text-foreground-muted uppercase tracking-wider text-[10px]">
                    Supplier
                  </span>
                  <span className="font-bold text-foreground truncate max-w-[200px] text-right">
                    {quoteToDelete.vendorName}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-bold text-foreground-muted uppercase tracking-wider text-[10px]">
                    Evaluated TCO
                  </span>
                  <span className="font-mono font-bold text-foreground">
                    {formatInr(quoteToDelete.evaluatedCost)}
                  </span>
                </div>
              </div>

              <p className="text-xs text-foreground-secondary leading-relaxed">
                Are you sure you want to withdraw or archive this quotation bid? It will be excluded from the side-by-side commercial comparison matrix for RFQ {quoteToDelete.rfqNumber}.
              </p>

              <div className="flex items-center justify-end gap-3 pt-2 border-t border-border/60">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={isDeleting}
                  onClick={() => setQuoteToDelete(null)}
                  className="cursor-pointer font-bold rounded-xl font-poppins"
                >
                  Cancel
                </Button>
                <button
                  type="button"
                  disabled={isDeleting}
                  onClick={handleConfirmDelete}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl bg-rose-600 hover:bg-rose-700 active:scale-95 text-white font-extrabold text-xs shadow-lg shadow-rose-600/30 transition-all cursor-pointer disabled:opacity-50 font-poppins"
                >
                  {isDeleting ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      <span>Withdrawing...</span>
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-3.5 w-3.5" />
                      <span>Withdraw Quotation</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* Right Slide-Over Curtain Drawer for Quotation Comparison Matrix */}
      <SlideOverDrawer
        isOpen={Boolean(selectedRfqForDrawer)}
        onClose={() => setSelectedRfqForDrawer(null)}
        title="Quotation Comparison Matrix"
        subtitle="Side-by-side commercial & technical bidder evaluation, landed TCO comparison, and AI scoring"
        fullPageUrl={selectedRfqForDrawer ? ROUTES.QUOTATIONS.COMPARISON(selectedRfqForDrawer) : undefined}
      >
        {selectedRfqForDrawer && (
          <QuotationComparisonPage
            rfqIdProp={selectedRfqForDrawer}
            isDrawer={true}
            onClose={() => setSelectedRfqForDrawer(null)}
          />
        )}
      </SlideOverDrawer>
    </div>
  );
};
